/**
 * Dettaglio di una rotazione generale (F-075).
 *
 * Mostra l'eletto di ogni gruppo **insieme ai non eletti e al perché**: senza
 * gli scartati la scelta sarebbe un nome e basta, indistinguibile da
 * un'estrazione a sorte. È anche ciò che permette di non essere d'accordo, che
 * è il motivo per cui una motivazione si scrive.
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '@/ui';
import { Markdown } from '@/ui/Markdown';
import { formatDateTime } from '@/lib/format';
import { useRotation } from './api';
import './discovery.css';

const STATUS_LABEL: Record<string, string> = {
  queued: 'In attesa',
  running: 'In corso',
  completed: 'Completata',
  failed: 'Non riuscita',
};

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'positive' | 'negative'> = {
  queued: 'neutral',
  running: 'accent',
  completed: 'positive',
  failed: 'negative',
};

export function DiscoveryRotationPage() {
  const { rotationId } = useParams<{ rotationId: string }>();
  const navigate = useNavigate();
  const rotation = useRotation(rotationId!);

  if (rotation.isLoading) return <Spinner label="Carico la rotazione…" />;
  if (rotation.isError || !rotation.data) {
    return (
      <div className="page">
        <EmptyState
          icon="🔁"
          title="Rotazione non trovata"
          description="Non esiste, oppure non è una delle tue."
          action={<Button onClick={() => navigate('/discovery')}>Torna a Scopri</Button>}
        />
      </div>
    );
  }

  const data = rotation.data;
  const running = data.status === 'queued' || data.status === 'running';

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1>Rotazione generale</h1>
          <p className="page__subtitle">
            Dalla ricerca <Link to={`/discovery/${data.runId}`}>{data.themeTitle}</Link>
          </p>
          <p className="cell--minor">Avviata {formatDateTime(data.createdAt)}</p>
        </div>
        <Badge tone={STATUS_TONE[data.status] ?? 'neutral'}>{STATUS_LABEL[data.status] ?? data.status}</Badge>
      </header>

      {running && (
        <Card>
          <div className="discovery-progress">
            <Spinner label={data.progress ?? 'In corso…'} />
            <p className="cell--minor">
              Un titolo eletto ogni cinque: la valutazione continua anche se chiudi la pagina.
            </p>
          </div>
        </Card>
      )}

      {data.status === 'failed' && (
        <Alert variant="error" title="La rotazione non è riuscita">
          {data.errorMessage ?? 'Errore non specificato.'}
        </Alert>
      )}

      {data.emptyReason && (
        <Alert variant="warning" title="Nessun titolo eletto">
          {data.emptyReason}
        </Alert>
      )}

      {/* Un gruppo che fallisce non porta via gli altri, ma la differenza va
          detta: altrimenti «due eletti su tre gruppi» sembra una scelta. */}
      {data.picksTotal > 0 && data.picksTotal < data.groupsTotal && (
        <Alert variant="warning" title="Valutazione incompleta">
          {data.groupsTotal - data.picksTotal} gruppi su {data.groupsTotal} non hanno prodotto una scelta.
          Puoi rilanciare la rotazione dalla pagina della ricerca.
        </Alert>
      )}

      {data.picks.length > 0 && (
        <div className="discovery-results">
          {data.picks.map((pick) => (
            <Card key={pick.id} flush>
              <article className="rotation-pick">
                <header className="rotation-pick__head">
                  <div>
                    <h3 className="rotation-pick__name">
                      {pick.instrumentId ? (
                        <Link to={`/instruments/${pick.instrumentId}`}>{pick.displayName}</Link>
                      ) : (
                        pick.displayName
                      )}
                    </h3>
                    <p className="cell--minor">
                      {pick.symbol ?? '—'} · eletto fra {pick.considered.length + 1} titoli del gruppo{' '}
                      {pick.groupIndex + 1}
                    </p>
                  </div>
                  <div className="discovery-card__badges">
                    {pick.potential !== null && <Badge tone="accent">Potenziale {pick.potential}/100</Badge>}
                    {pick.addedToWatchlistAt ? (
                      <Badge tone="positive">In watchlist ✓</Badge>
                    ) : (
                      <Badge tone="warning">Non aggiunto</Badge>
                    )}
                  </div>
                </header>

                <div className="discovery-card__rationale">
                  <Markdown content={pick.rationale} />
                </div>

                {pick.watchFor && (
                  <p className="discovery-card__risks">
                    <strong>Da osservare:</strong> {pick.watchFor}
                  </p>
                )}
                {pick.risks && (
                  <p className="discovery-card__risks">
                    <strong>Da tenere presente:</strong> {pick.risks}
                  </p>
                )}

                {pick.considered.length > 0 && (
                  <details className="rotation-pick__considered">
                    <summary>Perché non gli altri {pick.considered.length}</summary>
                    <ul>
                      {pick.considered.map((entry) => (
                        <li key={entry.ref}>
                          <strong>{entry.name}</strong>: {entry.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </article>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
